import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, Search, RefreshCw, Send, Loader2, Sparkles, 
  Trash2, CheckCircle, XCircle, Pause, Play, 
  Square, AlertTriangle, Calendar, DollarSign, TrendingDown,
  Lightbulb, Clock, Gift
} from 'lucide-react';
import ImageSelector from '@/components/ImageSelector';

export default function RecoveryPage() {
  const [loading, setLoading] = useState(true);
  const [expiredCustomers, setExpiredCustomers] = useState([]);
  const [connections, setConnections] = useState([]);
  const [plans, setPlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal de campanha de recuperação
  const [recoveryModalOpen, setRecoveryModalOpen] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [uniquePlans, setUniquePlans] = useState([]);
  const [planDiscounts, setPlanDiscounts] = useState({});
  
  // Formulário
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState(null);
  const [imageId, setImageId] = useState(null);
  const [delaySeconds, setDelaySeconds] = useState(5);
  const [connectionId, setConnectionId] = useState('');
  
  // IA
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  
  // Progresso de envio
  const [sending, setSending] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [campaignProgress, setCampaignProgress] = useState(null);
  const pollingRef = useRef(null);

  useEffect(() => {
    fetchData();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('nexus-token');
    const headers = { Authorization: `Bearer ${token}` };
    
    // Buscar clientes vencidos
    try {
      const customersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers?status=expired&limit=50000`, { headers });
      if (customersRes.ok) {
        const data = await customersRes.json();
        const expired = data.filter(c => c.status === 'expired');
        setExpiredCustomers(expired);
      }
    } catch (e) {
      console.error('Erro ao carregar clientes:', e);
    }
    
    // Buscar conexões
    try {
      const connectionsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections`, { headers });
      if (connectionsRes.ok) {
        const data = await connectionsRes.json();
        const connected = data.filter(c => c.status === 'connected');
        setConnections(connected);
        if (connected.length === 1) {
          setSelectedConnection(connected[0].id);
        }
      }
    } catch (e) {
      console.error('Erro ao carregar conexões:', e);
    }
    
    // Buscar planos
    try {
      const plansRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-plans`, { headers });
      if (plansRes.ok) {
        setPlans(await plansRes.json());
      }
    } catch (e) {
      console.error('Erro ao carregar planos:', e);
    }
    
    setLoading(false);
  };

  const filteredCustomers = expiredCustomers.filter(customer => {
    const term = searchTerm.toLowerCase();
    return customer.name?.toLowerCase().includes(term) ||
           customer.phone?.includes(term) ||
           customer.plan_name?.toLowerCase().includes(term);
  });

  const openRecoveryModal = () => {
    // Extrair planos únicos dos clientes vencidos
    const planMap = {};
    expiredCustomers.forEach(c => {
      if (c.plan_id && !planMap[c.plan_id]) {
        planMap[c.plan_id] = {
          id: c.plan_id,
          name: c.plan_name || 'Desconhecido',
          price: c.plan_price || 0,
          customerCount: 0
        };
      }
      if (planMap[c.plan_id]) {
        planMap[c.plan_id].customerCount++;
      }
    });
    
    const uniquePlansList = Object.values(planMap);
    setUniquePlans(uniquePlansList);
    
    // Inicializar descontos com preços originais
    const discounts = {};
    uniquePlansList.forEach(p => {
      discounts[p.id] = { newPrice: p.price, enabled: false };
    });
    setPlanDiscounts(discounts);
    
    // Selecionar todos os clientes por padrão
    setSelectedCustomers(expiredCustomers.map(c => c.id));
    
    setMessage('');
    setImageUrl(null);
    setImageId(null);
    setAiAnalysis(null);
    setRecoveryModalOpen(true);
    
    // Selecionar primeira conexão disponível
    if (connections.length > 0) {
      setConnectionId(connections[0].id);
    }
  };

  const generateAiAnalysis = async () => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      
      // Preparar dados para análise
      const analysisData = {
        total_expired: expiredCustomers.length,
        plans: uniquePlans.map(p => ({
          name: p.name,
          price: p.price,
          customer_count: p.customerCount
        })),
        customer_profiles: expiredCustomers.slice(0, 50).map(c => ({
          days_expired: Math.abs(Math.floor((new Date() - new Date(c.expiration_date)) / (1000 * 60 * 60 * 24))),
          plan_name: c.plan_name,
          is_sigma: !!c.sigma_username
        }))
      };
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recovery/ai-analysis`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(analysisData)
      });
      
      if (res.ok) {
        const data = await res.json();
        setAiAnalysis(data);
        toast.success('Análise de IA gerada!');
      } else {
        toast.error('Erro ao gerar análise');
      }
    } catch (error) {
      toast.error('Erro ao gerar análise de IA');
    } finally {
      setAiLoading(false);
    }
  };

  const improveMessageWithAi = async () => {
    if (!message.trim()) {
      toast.error('Digite uma mensagem primeiro');
      return;
    }
    
    setAiLoading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recovery/improve-message`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: message,
          context: {
            total_customers: selectedCustomers.length,
            plans: uniquePlans.filter(p => planDiscounts[p.id]?.enabled).map(p => ({
              name: p.name,
              original_price: p.price,
              new_price: planDiscounts[p.id]?.newPrice || p.price
            }))
          }
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessage(data.improved_message);
        toast.success('Mensagem melhorada pela IA!');
      } else {
        toast.error('Erro ao melhorar mensagem');
      }
    } catch (error) {
      toast.error('Erro ao processar com IA');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSendRecovery = async () => {
    if (!message.trim()) {
      toast.error('Digite uma mensagem');
      return;
    }
    if (!connectionId) {
      toast.error('Selecione uma conexão');
      return;
    }
    if (selectedCustomers.length === 0) {
      toast.error('Nenhum cliente selecionado');
      return;
    }
    
    if (!confirm(`Enviar mensagem de recuperação para ${selectedCustomers.length} cliente(s)?`)) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('nexus-token');
      
      // Preparar descontos (apenas os habilitados)
      const discountsToApply = {};
      Object.entries(planDiscounts).forEach(([planId, discount]) => {
        if (discount.enabled && discount.newPrice > 0) {
          const plan = uniquePlans.find(p => p.id === planId);
          if (plan && discount.newPrice < plan.price) {
            discountsToApply[planId] = {
              original_price: plan.price,
              new_price: Math.max(10, discount.newPrice) // Nunca abaixo de R$10
            };
          }
        }
      });
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recovery/send`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_ids: selectedCustomers,
          message: message,
          connection_id: connectionId,
          delay_seconds: delaySeconds,
          image_url: imageUrl,  // Já temos a URL da imagem selecionada
          plan_discounts: discountsToApply
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setRecoveryModalOpen(false);
        
        // Iniciar acompanhamento
        setActiveCampaign({
          id: data.campaign_id,
          total: data.total_customers
        });
        startPolling(data.campaign_id);
        
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao enviar');
      }
    } catch (error) {
      toast.error('Erro ao enviar campanha de recuperação');
    } finally {
      setSending(false);
    }
  };

  const startPolling = (campaignId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    
    fetchCampaignStatus(campaignId);
    pollingRef.current = setInterval(() => fetchCampaignStatus(campaignId), 2000);
  };

  const fetchCampaignStatus = async (campaignId) => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recovery/campaign/${campaignId}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCampaignProgress(data);
        
        if (['completed', 'stopped'].includes(data.status)) {
          clearInterval(pollingRef.current);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar status:', error);
    }
  };

  const pauseCampaign = async () => {
    if (!activeCampaign?.id) return;
    const token = localStorage.getItem('nexus-token');
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recovery/campaign/${activeCampaign.id}/pause`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    toast.success('Campanha pausada');
  };

  const resumeCampaign = async () => {
    if (!activeCampaign?.id) return;
    const token = localStorage.getItem('nexus-token');
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recovery/campaign/${activeCampaign.id}/resume`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    toast.success('Campanha retomada');
  };

  const stopCampaign = async () => {
    if (!activeCampaign?.id) return;
    if (!confirm('Tem certeza que deseja parar?')) return;
    const token = localStorage.getItem('nexus-token');
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recovery/campaign/${activeCampaign.id}/stop`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    toast.success('Campanha parada');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Recuperação de Clientes</h1>
          <p className="text-sm text-muted-foreground">
            {expiredCustomers.length === 0 
              ? 'Todos os clientes estão com a assinatura em dia!'
              : `${expiredCustomers.length} cliente(s) vencido(s) aguardando recuperação`
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button 
            onClick={openRecoveryModal} 
            disabled={expiredCustomers.length === 0}
            title={expiredCustomers.length === 0 ? 'Não há clientes vencidos para recuperar' : ''}
          >
            <Send className="w-4 h-4 mr-2" />
            Iniciar Recuperação
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={expiredCustomers.length === 0 ? "bg-green-500/10 border-green-500/20" : "bg-red-500/10 border-red-500/20"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${expiredCustomers.length === 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                {expiredCustomers.length === 0 ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                )}
              </div>
              <div>
                <p className={`text-2xl font-bold ${expiredCustomers.length === 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {expiredCustomers.length}
                </p>
                <p className="text-xs text-muted-foreground">Clientes Vencidos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/20">
                <DollarSign className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {formatCurrency(expiredCustomers.reduce((sum, c) => sum + (c.plan_price || 0), 0))}
                </p>
                <p className="text-xs text-muted-foreground">Receita Potencial</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{expiredCustomers.filter(c => c.sigma_username).length}</p>
                <p className="text-xs text-muted-foreground">Clientes Sigma</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <Clock className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {Math.round(expiredCustomers.reduce((sum, c) => {
                    const days = Math.abs(Math.floor((new Date() - new Date(c.expiration_date)) / (1000 * 60 * 60 * 24)));
                    return sum + days;
                  }, 0) / (expiredCustomers.length || 1))}
                </p>
                <p className="text-xs text-muted-foreground">Dias Vencidos (média)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Busca */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome, telefone ou plano..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Lista de Clientes Vencidos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Clientes Vencidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {expiredCustomers.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <p className="text-xl font-semibold text-foreground mb-2">Nenhum cliente vencido!</p>
              <p className="text-sm">Todos os seus clientes estão com a assinatura em dia.</p>
              <p className="text-sm mt-1">Isso é uma excelente notícia! 🎉</p>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum cliente encontrado com esse filtro</p>
              <p className="text-sm">Tente buscar por outro termo</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[500px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background border-b">
                  <tr>
                    <th className="text-left p-3">Cliente</th>
                    <th className="text-left p-3">Plano</th>
                    <th className="text-left p-3">Vencimento</th>
                    <th className="text-left p-3">Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map(customer => (
                    <tr key={customer.id} className="border-b hover:bg-muted/50">
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-xs text-muted-foreground">{customer.phone}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <div>
                          <p>{customer.plan_name}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(customer.plan_price || 0)}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge variant="destructive" className="text-xs">
                          {formatDate(customer.expiration_date)}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {customer.sigma_username ? (
                          <Badge variant="outline" className="text-xs">Sigma</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Manual</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Recuperação */}
      <Dialog open={recoveryModalOpen} onOpenChange={setRecoveryModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Campanha de Recuperação
            </DialogTitle>
            <DialogDescription>
              Envie uma mensagem persuasiva para trazer seus clientes de volta
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
            {/* Coluna Esquerda - Configurações */}
            <div className="space-y-4">
              {/* Conexão */}
              <div className="space-y-2">
                <Label>Conexão WhatsApp *</Label>
                <Select value={connectionId} onValueChange={setConnectionId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma conexão" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.map(conn => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {conn.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Mensagem */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Mensagem *</Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={improveMessageWithAi}
                    disabled={aiLoading || !message.trim()}
                  >
                    {aiLoading ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-1" />
                    )}
                    Melhorar com IA
                  </Button>
                </div>
                <Textarea
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Olá {nome}! Sentimos sua falta... Que tal voltar com uma oferta especial? Acesse seu link de renovação: {Link_de_pagamento}"
                />
                <p className="text-xs text-muted-foreground">
                  Variáveis: {'{nome}'}, {'{plano}'}, {'{valor}'}, {'{Link_de_pagamento}'}
                </p>
              </div>

              {/* Upload de Imagem */}
              <div className="space-y-2">
                <ImageSelector
                  value={imageUrl}
                  onChange={(url, id) => { setImageUrl(url); setImageId(id); }}
                  label="Imagem (opcional)"
                  category="campaign"
                />
                <p className="text-xs text-muted-foreground">
                  A imagem será enviada junto com a mensagem como legenda
                </p>
              </div>

              {/* Delay */}
              <div className="space-y-2">
                <Label>Intervalo entre envios (segundos)</Label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={delaySeconds}
                  onChange={(e) => setDelaySeconds(parseInt(e.target.value) || 5)}
                  className="w-24"
                />
              </div>
            </div>

            {/* Coluna Direita - Descontos e IA */}
            <div className="space-y-4">
              {/* Análise de IA */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-purple-500" />
                    <span className="font-medium">Análise Inteligente</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={generateAiAnalysis}
                    disabled={aiLoading}
                  >
                    {aiLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                
                {aiAnalysis ? (
                  <div className="space-y-3 text-sm">
                    {aiAnalysis.suggestions?.map((suggestion, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Gift className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                        <p>{suggestion}</p>
                      </div>
                    ))}
                    {aiAnalysis.recommended_discounts && (
                      <div className="mt-3 pt-3 border-t border-purple-500/20">
                        <p className="font-medium text-purple-600 mb-2">Descontos Recomendados:</p>
                        {Object.entries(aiAnalysis.recommended_discounts).map(([planName, discount]) => (
                          <p key={planName} className="text-xs text-muted-foreground">
                            • {planName}: {formatCurrency(discount)}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Clique no botão para gerar uma análise inteligente dos seus clientes e receber sugestões de recuperação.
                  </p>
                )}
              </div>

              {/* Descontos por Plano */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4" />
                  Descontos por Plano
                </Label>
                <p className="text-xs text-muted-foreground">
                  Defina preços promocionais por plano. Um cupom será criado automaticamente.
                </p>
                
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {uniquePlans.map(plan => (
                    <div key={plan.id} className="p-3 rounded-lg border bg-muted/30">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-sm">{plan.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {plan.customerCount} cliente(s) • {formatCurrency(plan.price)}
                          </p>
                        </div>
                        <Switch
                          checked={planDiscounts[plan.id]?.enabled || false}
                          onCheckedChange={(checked) => {
                            setPlanDiscounts(prev => ({
                              ...prev,
                              [plan.id]: { ...prev[plan.id], enabled: checked }
                            }));
                          }}
                        />
                      </div>
                      
                      {planDiscounts[plan.id]?.enabled && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm">Novo valor:</span>
                          <Input
                            type="number"
                            min="10"
                            max={plan.price - 1}
                            step="0.01"
                            value={planDiscounts[plan.id]?.newPrice || plan.price}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              setPlanDiscounts(prev => ({
                                ...prev,
                                [plan.id]: { ...prev[plan.id], newPrice: Math.max(10, val) }
                              }));
                            }}
                            className="w-28"
                          />
                          <Badge variant="secondary" className="text-xs">
                            -{formatCurrency(plan.price - (planDiscounts[plan.id]?.newPrice || plan.price))}
                          </Badge>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Resumo */}
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="text-sm font-medium mb-2">Resumo do Envio</p>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>• {selectedCustomers.length} clientes serão notificados</p>
                  <p>• {uniquePlans.filter(p => planDiscounts[p.id]?.enabled).length} plano(s) com desconto</p>
                  <p>• Intervalo: {delaySeconds} segundos entre envios</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecoveryModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSendRecovery} disabled={sending}>
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Iniciar Recuperação
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Progresso */}
      <Dialog open={!!activeCampaign} onOpenChange={(open) => !open && setActiveCampaign(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Progresso da Recuperação</DialogTitle>
          </DialogHeader>
          
          {campaignProgress && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <Badge variant={
                  campaignProgress.status === 'running' ? 'default' :
                  campaignProgress.status === 'paused' ? 'secondary' :
                  campaignProgress.status === 'completed' ? 'success' : 'destructive'
                }>
                  {campaignProgress.status === 'running' && 'Enviando...'}
                  {campaignProgress.status === 'paused' && 'Pausado'}
                  {campaignProgress.status === 'completed' && 'Concluído'}
                  {campaignProgress.status === 'stopped' && 'Parado'}
                </Badge>
                
                <div className="flex gap-2">
                  {campaignProgress.status === 'running' && (
                    <Button size="sm" variant="outline" onClick={pauseCampaign}>
                      <Pause className="w-4 h-4" />
                    </Button>
                  )}
                  {campaignProgress.status === 'paused' && (
                    <Button size="sm" variant="outline" onClick={resumeCampaign}>
                      <Play className="w-4 h-4" />
                    </Button>
                  )}
                  {['running', 'paused'].includes(campaignProgress.status) && (
                    <Button size="sm" variant="destructive" onClick={stopCampaign}>
                      <Square className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="space-y-1">
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all"
                    style={{ width: `${campaignProgress.progress}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{campaignProgress.progress}%</span>
                  <span>
                    <span className="text-green-500">{campaignProgress.sent_count} enviados</span>
                    {campaignProgress.failed_count > 0 && (
                      <span className="text-red-500 ml-2">{campaignProgress.failed_count} falhas</span>
                    )}
                  </span>
                </div>
              </div>
              
              {/* Lista de destinatários */}
              <div className="max-h-48 overflow-y-auto border rounded-lg">
                {campaignProgress.recipients?.slice(0, 20).map((r, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-2 text-sm border-b last:border-0 ${
                    r.status === 'sent' ? 'bg-green-500/5' :
                    r.status === 'failed' ? 'bg-red-500/5' : ''
                  }`}>
                    <span>{r.name}</span>
                    <span>
                      {r.status === 'sent' && <CheckCircle className="w-4 h-4 text-green-500" />}
                      {r.status === 'failed' && <XCircle className="w-4 h-4 text-red-500" />}
                      {r.status === 'pending' && idx === campaignProgress.current_index && (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveCampaign(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
